import i18n from '@/common/i18n';
import computedLocale from '@/common/utils/computedLocale';

export const decSuffixes = computedLocale(() => ({
  '-8': i18n.t('yoctoShort'),
  '-7': i18n.t('zeptoShort'),
  '-6': i18n.t('attoShort'),
  '-5': i18n.t('femtoShort'),
  '-4': i18n.t('picoShort'),
  '-3': i18n.t('nanoShort'),
  '-2': i18n.t('microShort'),
  '-1': i18n.t('milliShort'),
  0: '',
  1: i18n.t('KiloShort'),
  2: i18n.t('MegaShort'),
  3: i18n.t('GigaShort'),
  4: i18n.t('TeraShort'),
  5: i18n.t('PetaShort'),
  6: i18n.t('ExaShort'),
  7: i18n.t('ZettaShort'),
  8: i18n.t('YottaShort')
}));

export const binSuffixes = computedLocale(() => ({
  0: '',
  1: i18n.t('KibiShort'),
  2: i18n.t('MibiShort'),
  3: i18n.t('GibiShort'),
  4: i18n.t('TibiShort'),
  5: i18n.t('PibiShort'),
  6: i18n.t('EibiShort'),
  7: i18n.t('ZibiShort'),
  8: i18n.t('YibiShort')
}));

export const BYTES_IN_MIBIBYTES = 1024 * 1024;
export const BYTES_IN_GIBIBYTES = 1024 * 1024 * 1024;
export const TEBIBYTE_IN_GIBIBYTES = 1024;
export const MEBIBYTES_IN_GIBIBYTE = 1024;

export const BYTES_IN_MEGABYTES = 1000 * 1000;
export const BYTES_IN_GIGABYTES = 1000 * 1000 * 1000;
export const TERABYTE_IN_GIGABYTES = 1000;
export const MEGABYTES_IN_GIGABYTE = 1000;

export function roundNumber(value: number, decimalPlaces = 2): number {
  if (!isFinite(value)) {
    return 0;
  }
  // Important: Math.round((2.3 + .9) * 100) / 100 !== Math.round((2.3 + .9))
  const placesMultiplier = 10 ** decimalPlaces;

  return Math.round(placesMultiplier * value) / placesMultiplier;
}

export function formatPercent(value: number, round = 2): string {
  const num = Math.round((value * (10 ** round))) / (10 ** round);
  return `${i18n.n(num)}%`;
}

/** Percent wrapped in parentheses (e.g. `(42%)`). Prefer over `'(' + formatPercent(...) + ')'` in templates for readability and eslint. */
export function formatPercentParen(value: number, round = 2): string {
  return `(${formatPercent(value, round)})`;
}

export function toUnit(value: number | null | undefined, argFormat: Partial<UnitFormat>): ToUnitReturn {
  const format = {
    base: 1024,
    hasSuffix: true,
    suffixes: binSuffixes,
    resultString: true,
    ...argFormat
  };

  if (!value || !isFinite(value)) {
    value = 0;
  }

  let pow = value !== 0
    ? Math.floor(Math.log(Math.abs(value)) / (Math.log(format.base)) || 0)
    : 0;

  if (format.minPow) {
    const minPowConsSuff = format.minPow;

    if (Number.isFinite(minPowConsSuff)) {
      pow = Math.max(pow, minPowConsSuff);
    }
  }

  if (format.maxPow) {
    const maxPowConsSuff = format.maxPow;

    if (Number.isFinite(maxPowConsSuff)) {
      pow = Math.min(pow, maxPowConsSuff);
    }
  }

  let roundBy;
  if (typeof format.round === 'undefined') {
    if (Math.floor(value / (10 * (format.base ** pow))) === 0) {
      roundBy = 2;
    } else if (Math.floor(value / (100 * (format.base ** pow))) === 0) {
      roundBy = 1;
    } else {
      roundBy = 0;
    }
  } else {
    roundBy = format.round;
  }

  let num = Math.round((value * (10 ** roundBy)) / (format.base ** pow)) / (10 ** roundBy);
  let suffix = format.suffixes[pow] as string;

  if (suffix === undefined) {
    pow = Math.floor(Math.log(Math.abs(value)) / (Math.log(10)) || 0);
    num = Math.round((value / (10 ** pow)) * (10 ** roundBy)) / (10 ** roundBy);
    if (pow > 0) {
      suffix = `E+${pow} `;
    } else {
      suffix = `E${pow} `;
    }
  }

  if (format.resultString) {
    return !format.hasSuffix ? i18n.n(num) : `${i18n.n(num)} ${suffix}`;
  }

  return {
    valueNumeric: num,
    value: i18n.n(num),
    suffix: !format.hasSuffix ? '' : suffix
  };
}

export function toDecUnit(value: number | null | undefined, format?: Partial<Omit<UnitFormat, 'suffixes' | 'base'>>): ToUnitReturn {
  return toUnit(value, {
    base: 1000,
    hasSuffix: true,
    suffixes: decSuffixes,
    resultString: true,
    ...format
  });
}

export function toBinUnit(value: number | null | undefined, format?: Partial<Omit<UnitFormat, 'suffixes' | 'base'>>): ToUnitReturn {
  return toUnit(value, {
    base: 1024,
    hasSuffix: true,
    suffixes: binSuffixes,
    resultString: true,
    ...format
  });
}

export function bytesToUnit(bytes: number | null | undefined, options: Partial<Omit<UnitFormat, 'hasSuffix'>> & { resultString: false }): ToUnitObject;
export function bytesToUnit(bytes: number | null | undefined, options?: Partial<Omit<UnitFormat, 'hasSuffix'>> & { resultString?: true }): string;
export function bytesToUnit(bytes, {
  minPow = undefined,
  maxPow = undefined,
  round,
  base = 1024,
  suffixes = binSuffixes,
  resultString = true
}: Partial<Omit<UnitFormat, 'hasSuffix'>> = {}): ToUnitReturn {
  const preformat = toUnit(bytes, {
    minPow,
    maxPow,
    round,
    base,
    hasSuffix: true,
    suffixes,
    resultString: false,
  });

  if (typeof preformat !== 'string') {
    if (preformat.suffix) {
      preformat.suffix += i18n.t('bytesShort');
    } else {
      preformat.suffix = i18n.t('bytes');
    }

    if (resultString) {
      return `${preformat.value} ${preformat.suffix}`;
    }
  }

  return preformat;
}

export function bytesToUnitObject(bytes: number | null | undefined, format?: Partial<Omit<UnitFormat, 'hasSuffix' | 'resultString'>>): ToUnitObject {
  return bytesToUnit(bytes, {
    resultString: false,
    ...format
  });
}

export function MiBToUnit(mib: number | null | undefined, format: Partial<Pick<UnitFormat, 'round' | 'resultString' | 'minPow' | 'maxPow'>> & { resultString: false }): ToUnitObject;
export function MiBToUnit(mib: number | null | undefined, format?: Partial<Pick<UnitFormat, 'round' | 'resultString' | 'minPow' | 'maxPow'>> & { resultString?: true }): string;
export function MiBToUnit(mib, format?: Partial<Pick<UnitFormat, 'round' | 'resultString' | 'minPow' | 'maxPow'>>): ToUnitReturn {
  return bytesToUnit(mib * BYTES_IN_MIBIBYTES, format as any);
}

export function MiBToUnitObject(mib, format?: Partial<Pick<UnitFormat, 'minPow' | 'maxPow' | 'round'>>): ToUnitReturn {
  return MiBToUnit(mib, { resultString: false, ...format });
}

export function GiBToUnit(gib: number | null | undefined, format: Partial<Pick<UnitFormat, 'minPow' | 'maxPow' | 'round' | 'resultString'>> & { resultString: false }): ToUnitObject;
export function GiBToUnit(gib: number | null | undefined, format?: Partial<Pick<UnitFormat, 'minPow' | 'maxPow' | 'round' | 'resultString'>> & { resultString?: true }): string;
export function GiBToUnit(gib, format?: Partial<Pick<UnitFormat, 'minPow' | 'maxPow' | 'round' | 'resultString'>>): ToUnitReturn {
  return bytesToUnit(gib * BYTES_IN_GIBIBYTES, format as any);
}

export function GiBToUnitObject(gib, format?: Partial<Pick<UnitFormat, 'minPow' | 'maxPow' | 'round'>>): ToUnitReturn {
  return GiBToUnit(gib, { resultString: false, ...format });
}

export function MBToUnit(mib: number | null | undefined, format: Partial<Pick<UnitFormat, 'minPow' | 'maxPow' | 'round' | 'resultString'>> & { resultString: false }): ToUnitObject;
export function MBToUnit(mib: number | null | undefined, format?: Partial<Pick<UnitFormat, 'minPow' | 'maxPow' | 'round' | 'resultString'>> & { resultString?: true }): string;
export function MBToUnit(mib, format?: Partial<Pick<UnitFormat, 'minPow' | 'maxPow' | 'round' | 'resultString'>>): ToUnitReturn {
  return bytesToUnit(mib * BYTES_IN_MEGABYTES, { base: 1000, suffixes: decSuffixes, ...format } as any);
}

export function formatBitrate(
  bitsPerSecond: number | null | undefined,
  format?: Partial<Pick<UnitFormat, 'minPow' | 'maxPow' | 'round'>>
): string {
  const preformat = toUnit(bitsPerSecond, {
    base: 1000,
    hasSuffix: true,
    suffixes: decSuffixes,
    resultString: false,
    ...format
  });

  if (typeof preformat === 'string') {
    return preformat;
  }

  return `${preformat.value} ${preformat.suffix}${i18n.t('bits/sShort')}`;
}
